import React, { useState, useCallback, useEffect, useMemo } from 'react'
import {
  Alert, Box, Grid, Typography, Button, Chip, Stack, IconButton, Tooltip, TextField, InputAdornment,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MenuBook as MenuBookIcon,
  Checklist as ChecklistIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  FactCheck as FactCheckIcon,
  Sync as SyncIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import PageHeader from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TabelaAcao, ColunaTabelaAcao } from '@/components/common/TabelaAcao'
import MaterialDialog from '@/components/materiais/MaterialDialog'
import { produtoService } from '@/services/produtoService'
import { mensagemErroProdutoP1 } from '@/constants/catalogoProdutoP1'
import { resumoService } from '@/services/resumoService'
import { conteudosService } from '@/services/conteudosService'
import { Produto } from '@/types/produto'
import { CatalogoProdutosResumo } from '@/types/resumos'
import type { ConteudoPublicado, TipoConteudoPublicado } from '@/types/conteudo'
import { TIPOS_CONTEUDO } from '@/types/conteudo'
import { authStore } from '@/store/authStore'
import { ehConsultor, papelPrincipal } from '@/components/layout/menu'
import { informativoProdutoService } from '@/services/informativoProdutoService'
import { useBaixarManual } from '@/hooks/useBaixarManual'
import type { InformativoVigente } from '@/types/informativo'
import { CATEGORIAS_ESPECIAIS, CATEGORIAS_PRODUTO, rotuloCategoria } from '@/constants/categoriasProduto'
import { CODIGO_OUTRA_NECESSIDADE, codigoCanonico } from '@/constants/codigosProduto'
import { ChipStatusProduto } from '@/components/produtos/StatusProdutoCatalogo'
import { tokens } from '@/theme'
import { ROTULO_TIPO_ELEGIBILIDADE } from '@/types/elegibilidade'
import { fracaoComoPercentual } from '@/components/elegibilidade/formatoElegibilidade'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value)
}

/**
 * Regra de elegibilidade VIGENTE do produto (P2: `ProdutoResumoResponse.elegibilidade`, a política versionada).
 * Só apresentação: rótulo do tipo e o parâmetro publicado em R$ ou % (fração → %). Nada calculado.
 * P6 (AN-PP6 §7): as colunas V9 (`faturamentoAnualMinimo/Maximo` do produto) saíram do backend.
 */
export const regraElegibilidade = (p: Pick<Produto, 'elegibilidade'>): string => {
  const e = p.elegibilidade
  if (!e?.tipo) return 'Sem política de elegibilidade vigente'
  const tipo = ROTULO_TIPO_ELEGIBILIDADE[e.tipo] ?? e.tipo
  switch (e.tipo) {
    case 'REVENUE_MIN':
      return e.faturamentoAnualMinimo != null ? `${tipo}: ${formatCurrency(e.faturamentoAnualMinimo)}` : tipo
    case 'RECEIVABLES_RANGE':
      return e.valorOperacaoMinimo != null ? `${tipo}: a partir de ${formatCurrency(e.valorOperacaoMinimo)}` : tipo
    case 'LTV_MAX':
      return e.ltvMaximo != null ? `${tipo}: ${fracaoComoPercentual(e.ltvMaximo)}` : tipo
    default:
      return tipo
  }
}

/**
 * P1 (AN-PP1 §3.2 e §2.1): o que fica fora de "Produtos e Materiais" do CONSULTOR — "Outra
 * necessidade" (opção especial da Nova Reserva, não produto de prateleira; decidido pelo código
 * ou, sem código, pela categoria) e produto RETIRADA (o backend já filtra; defesa da tela).
 */
export const foraDoCatalogoDoConsultor = (p: Pick<Produto, 'codigo' | 'categoria' | 'status'>): boolean =>
  p.codigo === CODIGO_OUTRA_NECESSIDADE ||
  (!p.codigo && CATEGORIAS_ESPECIAIS.has(p.categoria)) ||
  p.status === 'RETIRADA'

/** Motivo de o ADMIN não poder excluir (null = pode). Canônico ou retirado → use a retirada (§1.3). */
export const motivoNaoExcluivel = (p: Pick<Produto, 'codigo' | 'status'>): string | null => {
  if (p.status === 'RETIRADA') return 'Produto retirado: fica como histórico e não pode ser excluído.'
  if (codigoCanonico(p.codigo)) return 'Produto do catálogo canônico não é excluído — use "Alterar status" → Retirada.'
  return null
}

/** Ícone por tipo de peça (Figma: Guia · Checklist · Roteiro). */
const ICONE_MATERIAL: Record<TipoConteudoPublicado, React.ReactNode> = {
  GUIA_COMERCIAL: <MenuBookIcon fontSize="small" />,
  CHECKLIST_DOCUMENTAL: <ChecklistIcon fontSize="small" />,
  ROTEIRO_REUNIAO: <RecordVoiceOverIcon fontSize="small" />,
}

const ORDEM_TIPO: TipoConteudoPublicado[] = ['GUIA_COMERCIAL', 'CHECKLIST_DOCUMENTAL', 'ROTEIRO_REUNIAO']

/**
 * Materiais vigentes que valem para o produto: os publicados para ele mais os da
 * plataforma (`produtoId` nulo = todos os produtos). Ordenados Guia · Checklist · Roteiro.
 */
export const materiaisDoProduto = (
  produtoId: string,
  vigentes: ConteudoPublicado[],
): ConteudoPublicado[] =>
  vigentes
    // `== null`: o backend pode omitir o campo (undefined) em vez de mandar null — vale para todos.
    .filter((c) => c.produtoId === produtoId || c.produtoId == null)
    .sort((a, b) => ORDEM_TIPO.indexOf(a.tipo) - ORDEM_TIPO.indexOf(b.tipo))

/** Motivo do "Informativo indisponível" (tooltip da ação desabilitada da `TabelaAcao`). */
export const MOTIVO_SEM_INFORMATIVO = 'Este produto ainda não tem PDF informativo publicado.'

/**
 * Produtos e Materiais (`/produtos-materiais`, Figma — Onda 8 / A16).
 *
 * Tabela Produto · Elegibilidade/range · Comissão · Documentação · Materiais; o detalhe do
 * produto (abas) continua por `Abrir →`. KPIs de `GET /v1/produtos/resumo` (A03).
 *
 * - Comissão é o TEXTO da regra cadastrada no produto, nunca número calculado. O
 *   `ProdutoResponse` ainda não traz esse texto (lacuna relatada): a coluna mostra "—".
 * - Materiais vêm de `GET /v1/conteudos/vigentes` (A12) — escopo do perfil/tenant no
 *   backend; o PDF abre no modal "Visualização de material" pelo endpoint auditado de
 *   documentos. Se a lista de materiais falhar, o catálogo continua e a coluna avisa.
 * - Onda 5 (AN-11 §1.4, L16): para o CONSULTOR, "Abrir →" baixa SÓ o PDF informativo
 *   vigente publicado pelo ADMIN (`GET /v1/documentos/informativos-vigentes`); sem vigente, o
 *   botão fica desabilitado com "Informativo indisponível". O back-office continua indo ao
 *   detalhe (aba Informativo).
 * - L12 (Onda 4, Q34 da Especificação Definitiva): o CONSULTOR vê "somente informações
 *   resumidas do produto e um PDF informativo" — nenhuma coluna/ação de Materiais (guia
 *   comercial, checklist documental, roteiro de reunião), nenhum KPI de gestão do catálogo
 *   (contam inativos que a lista dele não traz) e só produtos ativos (o backend força).
 */
const ProdutosListPage: React.FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const isAdmin = !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN')
  /**
   * Persona, não permissão: quem chega pelo menu do back-office ("Catálogo de Produtos")
   * vê o título daquele item, mesmo sem poder editar — só ADMIN edita, e as ações já se
   * escondem sozinhas por `isAdmin`.
   */
  const papel = papelPrincipal(usuario?.permissoes)
  const ehBackOffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'
  const consultor = ehConsultor(usuario?.permissoes)
  /** Informativo vigente por produto (só consultor). Falha ao listar = nenhum disponível. */
  const [informativos, setInformativos] = useState<Map<string, InformativoVigente>>(new Map())
  const {
    baixar: baixarManual,
    baixandoId,
    erro: erroManual,
    limparErro: limparErroManual,
  } = useBaixarManual()
  const [busca, setBusca] = useState('')
  const [categoria, setCategoria] = useState<string | null>(null)
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [materiais, setMateriais] = useState<ConteudoPublicado[]>([])
  const [materiaisIndisponiveis, setMateriaisIndisponiveis] = useState(false)
  const [resumo, setResumo] = useState<CatalogoProdutosResumo | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)
  const [materialAberto, setMaterialAberto] = useState<ConteudoPublicado | null>(null)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    setErroStatus(null)
    try {
      const [page, kpis, vigentes, informativosDoCatalogo] = await Promise.all([
        produtoService.listar(0, 100),
        // KPIs do catálogo (A03) — só back-office; falha SÓ no resumo não derruba a lista.
        consultor ? Promise.resolve(null) : resumoService.produtos().catch(() => null),
        // Materiais (A12) — só back-office (L12: o consultor não vê Materiais). Falha aqui não
        // derruba o catálogo: a coluna avisa.
        consultor
          ? Promise.resolve([] as ConteudoPublicado[])
          : conteudosService.vigentes().catch(() => null),
        // Informativos vigentes (Onda 5) — só o consultor usa; falha deixa "indisponível".
        consultor
          ? informativoProdutoService.vigentes().catch(() => new Map<string, InformativoVigente>())
          : Promise.resolve(new Map<string, InformativoVigente>()),
      ])
      setProdutos(page.content ?? [])
      setResumo(kpis)
      setMateriais(vigentes ?? [])
      setMateriaisIndisponiveis(vigentes === null)
      setInformativos(informativosDoCatalogo)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar produtos')
      setErroStatus((e as { status?: number })?.status ?? null)
    } finally {
      setLoading(false)
    }
  }, [consultor])

  useEffect(() => {
    carregar()
  }, [carregar])

  /**
   * Onda 5 (AN-11 §4, DEV-S): republica `produto-projetado` de todo o catálogo — o runbook
   * usa quando a projeção de oportunidades não bate com o catálogo. Só ADMIN.
   */
  const [ressincronizando, setRessincronizando] = useState(false)
  const handleRessincronizar = async () => {
    setRessincronizando(true)
    try {
      const r = await produtoService.ressincronizarProjecao()
      const n = r?.produtosRepublicados ?? 0
      enqueueSnackbar(`${n} produto${n === 1 ? '' : 's'} republicado${n === 1 ? '' : 's'} para as reservas.`, {
        variant: 'success',
      })
    } catch (e) {
      const m = (e as { message?: unknown } | null)?.message
      enqueueSnackbar(typeof m === 'string' && m ? m : 'Falha ao ressincronizar os produtos', { variant: 'error' })
    } finally {
      setRessincronizando(false)
    }
  }

  const handleRemover = async (produto: Produto) => {
    if (!window.confirm(`Remover o produto "${produto.nome}"?`)) return
    try {
      await produtoService.deletar(produto.id)
      enqueueSnackbar('Produto removido', { variant: 'success' })
      await carregar()
    } catch (e) {
      enqueueSnackbar(mensagemErroProdutoP1(e, 'Falha ao remover produto'), { variant: 'error' })
    }
  }

  const produtosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    return produtos.filter(
      (p) =>
        (!consultor || !foraDoCatalogoDoConsultor(p)) &&
        (!categoria || p.categoria === categoria) &&
        (!termo ||
          p.nome.toLowerCase().includes(termo) ||
          (p.codigo ?? '').toLowerCase().includes(termo) ||
          (p.descricao ?? '').toLowerCase().includes(termo) ||
          rotuloCategoria(p.categoria).toLowerCase().includes(termo)),
    )
  }, [produtos, busca, categoria, consultor])

  const limparFiltros = () => {
    setBusca('')
    setCategoria(null)
  }

  const filtrosAtivos = !!categoria || busca.trim().length > 0

  const colunas: ColunaTabelaAcao<Produto>[] = [
    {
      titulo: 'Produto',
      render: (p) => (
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.nome}</Typography>
            {/* P1 (AN-PP1 §2.1): chip do status normativo; "Ativa" não precisa de selo na prateleira do consultor. */}
            {p.status && (!consultor || p.status !== 'ATIVA') && <ChipStatusProduto status={p.status} />}
          </Stack>
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
            {/* Código estável (AN-PP1 §1): referência do back-office. */}
            {!consultor && p.codigo && (
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{p.codigo} · </Box>
            )}
            {rotuloCategoria(p.categoria)}
            {!p.ativo && ' · inativo'}
          </Typography>
        </Box>
      ),
    },
    {
      // P1/P6 (AN-PP1 §6, AN-PP6 §7): faixas de valor/prazo/taxa e colunas V9 saíram. A coluna mostra
      // a política de elegibilidade vigente (a que o motor avalia) e os critérios descritivos.
      titulo: 'Elegibilidade',
      render: (p) => (
        <Box>
          <Typography variant="body2">{regraElegibilidade(p)}</Typography>
          <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
            {p.criteriosElegibilidade?.length
              ? `${p.criteriosElegibilidade.length} critério(s)`
              : 'sem critérios cadastrados'}
          </Typography>
        </Box>
      ),
    },
    {
      titulo: 'Comissão',
      render: (p) =>
        p.regraComissao ? (
          <Typography variant="body2">{p.regraComissao}</Typography>
        ) : (
          <Tooltip title="Regra de comissão ainda não cadastrada no produto.">
            <span>—</span>
          </Tooltip>
        ),
    },
    {
      titulo: 'Documentação',
      render: (p) => {
        const n = p.documentacaoNecessaria?.length ?? 0
        return n > 0 ? `${n} ${n === 1 ? 'item' : 'itens'}` : '—'
      },
    },
    // L12: a coluna Materiais (guia, checklist, roteiro) é só do back-office.
    ...(consultor ? [] : [{
      titulo: 'Materiais',
      render: (p: Produto) => {
        const lista = materiaisDoProduto(p.id, materiais)
        if (materiaisIndisponiveis) {
          return (
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              indisponíveis
            </Typography>
          )
        }
        if (lista.length === 0) return '—'
        return (
          <Stack direction="row" spacing={0.5}>
            {lista.map((c) => (
              <Tooltip key={c.id} title={`${TIPOS_CONTEUDO[c.tipo]}: ${c.titulo}`}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`${TIPOS_CONTEUDO[c.tipo]}: ${c.titulo}`}
                  onClick={() => setMaterialAberto(c)}
                >
                  {ICONE_MATERIAL[c.tipo]}
                </IconButton>
              </Tooltip>
            ))}
          </Stack>
        )
      },
    }]),
    ...(isAdmin
      ? [
          {
            titulo: 'Gestão',
            align: 'right' as const,
            render: (p: Produto) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                {/* Atalho direto (08/09): o negócio não achava a aba pelo menu — o caminho era
                    "Abrir →" na linha e depois a aba. Abre o produto já na Elegibilidade. */}
                <Tooltip title="Elegibilidade e checklist documental">
                  <IconButton
                    size="small"
                    aria-label={`Elegibilidade e checklist de ${p.nome}`}
                    onClick={() => navigate(`/produtos/${p.id}?aba=elegibilidade`)}
                  >
                    <FactCheckIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Editar">
                  <IconButton size="small" aria-label={`Editar ${p.nome}`} onClick={() => navigate(`/produtos/${p.id}/editar`)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* P1 (AN-PP1 §1.3): canônico ou retirado não se exclui (422 no servidor) — a saída é a retirada. */}
                <Tooltip title={motivoNaoExcluivel(p) ?? 'Remover'}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Remover ${p.nome}`}
                      disabled={!!motivoNaoExcluivel(p)}
                      onClick={() => void handleRemover(p)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            ),
          },
        ]
      : []),
  ]

  return (
    <Box>
      {/*
        Mesma página, duas portas de entrada: o consultor chega por "Produtos e Materiais"
        (consulta) e o back-office por "Catálogo de Produtos" (cadastro). O título segue o
        item de menu que trouxe o usuário — cabeçalho que contradiz o menu confunde.
      */}
      <PageHeader
        title={ehBackOffice ? 'Catálogo de Produtos' : 'Produtos e Materiais'}
        subtitle={
          ehBackOffice
            ? 'Cadastro dos produtos, das regras que o motor aplica e dos materiais de apoio'
            : 'Informações resumidas dos produtos e o PDF informativo de cada um'
        }
        action={
          isAdmin ? (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Republica o status de todos os produtos para o módulo de reservas">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<SyncIcon />}
                    disabled={ressincronizando}
                    onClick={() => void handleRessincronizar()}
                  >
                    {ressincronizando ? 'Ressincronizando…' : 'Ressincronizar'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/produtos/novo')}
              >
                Novo Produto
              </Button>
            </Stack>
          ) : undefined
        }
      />

      {erro && (
        <EstadoDeLista
          estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
          {...(erroStatus !== 403 && {
            mensagem: 'Não foi possível carregar os produtos',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
        />
      )}

      {!erro && !consultor && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/produtos/resumo (total) — mesmo catálogo da lista abaixo */}
            <KpiCard
              label="Produtos"
              valor={resumo?.total ?? '—'}
              legenda="No catálogo"
              variante="escura"
              carregando={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/produtos/resumo (ativos) */}
            <KpiCard label="Ativos" valor={resumo?.ativos ?? '—'} carregando={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/produtos/resumo (comParceiro) */}
            <KpiCard label="Com parceiro" valor={resumo?.comParceiro ?? '—'} carregando={loading} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/produtos/resumo (comChecklist — documentação do catálogo) */}
            <KpiCard
              label="Com checklist"
              valor={resumo?.comChecklist ?? '—'}
              legenda="Documentação cadastrada"
              carregando={loading}
            />
          </Grid>
        </Grid>
      )}

      {!erro && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Buscar produto…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Buscar produto' }}
            sx={{ minWidth: 240 }}
          />
          {CATEGORIAS_PRODUTO.map((cat) => (
            <Chip
              key={cat.value}
              label={cat.label}
              size="small"
              clickable
              color={categoria === cat.value ? 'primary' : 'default'}
              variant={categoria === cat.value ? 'filled' : 'outlined'}
              onClick={() => setCategoria((atual) => (atual === cat.value ? null : cat.value))}
            />
          ))}
        </Stack>
      )}

      {erro ? null : loading ? (
        <EstadoDeLista estado="carregando" mensagem="Carregando produtos…" />
      ) : produtosFiltrados.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum produto encontrado"
          detalhe={
            filtrosAtivos
              ? 'Ajuste os filtros para ver mais produtos.'
              : 'O catálogo é mantido pelo ADMIN — sem produtos cadastrados, nada a listar.'
          }
          acao={filtrosAtivos ? { label: 'Limpar filtros', onClick: limparFiltros } : undefined}
        />
      ) : (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {produtosFiltrados.length} produto{produtosFiltrados.length !== 1 ? 's' : ''} encontrado{produtosFiltrados.length !== 1 ? 's' : ''}
              {filtrosAtivos && <Button size="small" onClick={limparFiltros} sx={{ ml: 1 }}>Limpar filtros</Button>}
            </Typography>
          </Box>
          {erroManual && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={limparErroManual}>
              {erroManual}
            </Alert>
          )}
          {consultor ? (
            // Onda 5: "Abrir →" baixa o informativo vigente; sem ele, a ação fica desabilitada com o motivo.
            <TabelaAcao<Produto>
              aria-label="Produtos e materiais"
              colunas={colunas}
              linhas={produtosFiltrados}
              chaveDaLinha={(p) => p.id}
              onAbrir={(p) => {
                const informativo = informativos.get(p.id)
                if (informativo) void baixarManual(informativo)
              }}
              acaoDesabilitada={(p) => {
                const informativo = informativos.get(p.id)
                if (!informativo) return MOTIVO_SEM_INFORMATIVO
                return baixandoId === informativo.documentoId ? 'Abrindo o informativo…' : false
              }}
              renderAcao={(p) => {
                const informativo = informativos.get(p.id)
                if (!informativo) return 'Informativo indisponível'
                return baixandoId === informativo.documentoId ? 'Abrindo…' : 'Abrir →'
              }}
              rotuloAcessivelAcao={(p) => (informativos.has(p.id) ? `Abrir informativo de ${p.nome}` : undefined)}
            />
          ) : (
            <TabelaAcao<Produto>
              aria-label="Produtos e materiais"
              colunas={colunas}
              linhas={produtosFiltrados}
              chaveDaLinha={(p) => p.id}
              onAbrir={(p) => navigate(`/produtos/${p.id}`)}
            />
          )}
        </>
      )}

      {!consultor && (
        <MaterialDialog conteudo={materialAberto} onClose={() => setMaterialAberto(null)} />
      )}
    </Box>
  )
}

export default ProdutosListPage
